import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useHousehold } from '@/context/HouseholdContext'

// Orçamento por categoria/mês (amount_base = na moeda base do household).
// month = primeiro dia do mês (YYYY-MM-01).
export function monthKeyISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

// "Mês" sentinela onde guardamos os orçamentos PREDEFINIDOS (template), fora de
// qualquer mês real. "Aplicar predefinidos" copia daqui para o mês selecionado.
export const PRESET_MONTH = '2000-01-01'

// Lê os orçamentos do mês: Map<category_id, amount_base>.
export function useBudgets(monthISO: string) {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['budgets', householdId, monthISO],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('budgets')
        .select('category_id, amount_base')
        .eq('household_id', householdId)
        .eq('month', monthISO)
      if (error) throw error
      const map = new Map<string, number>()
      for (const b of data ?? []) map.set(b.category_id as string, Number(b.amount_base))
      return map
    },
  })
}

// Orçamentos de TODOS os meses reais (exclui o mês-sentinela dos predefinidos).
// Usado para encadear a sobra: em meses futuros o resultado do mês é o ORÇADO,
// não o consumido (que ainda está incompleto).
export function useAllBudgets() {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['budgets', householdId, 'all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('budgets')
        .select('month, category_id, amount_base')
        .eq('household_id', householdId)
        .neq('month', PRESET_MONTH)
      if (error) throw error
      return (data ?? []) as { month: string; category_id: string; amount_base: number }[]
    },
  })
}

// Define/atualiza (ou remove, se 0) o orçamento de uma categoria no mês.
export function useSaveBudget() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ categoryId, month, amount }: { categoryId: string; month: string; amount: number }) => {
      if (amount <= 0.005) {
        const { error } = await supabase
          .from('budgets')
          .delete()
          .eq('household_id', householdId)
          .eq('category_id', categoryId)
          .eq('month', month)
        if (error) throw error
      } else {
        const { error } = await supabase
          .from('budgets')
          .upsert(
            { household_id: householdId, category_id: categoryId, month, amount_base: Math.round(amount * 100) / 100 },
            { onConflict: 'household_id,category_id,month' },
          )
        if (error) throw error
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['budgets'] }),
  })
}

// Grava vários orçamentos de uma vez (um único upsert). Usado pelo "Ajuste",
// que pode tocar dezenas de categorias — uma requisição por categoria estouraria.
export function useUpsertBudgets() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (rows: { categoryId: string; month: string; amount: number }[]) => {
      if (rows.length === 0) return 0
      const payload = rows.map((r) => ({
        household_id: householdId,
        category_id: r.categoryId,
        month: r.month,
        amount_base: Math.round(r.amount * 100) / 100,
      }))
      const { error } = await supabase.from('budgets').upsert(payload, { onConflict: 'household_id,category_id,month' })
      if (error) throw error
      return payload.length
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['budgets'] }),
  })
}

// Copia todos os orçamentos de um mês (from) para outro (to). Sobrescreve os
// que já existirem no destino (upsert). Retorna quantos foram copiados.
export function useCopyBudget() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ from, to }: { from: string; to: string }) => {
      const { data, error } = await supabase
        .from('budgets')
        .select('category_id, amount_base')
        .eq('household_id', householdId)
        .eq('month', from)
      if (error) throw error
      const rows = (data ?? []).map((b) => ({
        household_id: householdId,
        category_id: b.category_id as string,
        month: to,
        amount_base: Number(b.amount_base),
      }))
      if (rows.length === 0) return 0
      const { error: upErr } = await supabase.from('budgets').upsert(rows, { onConflict: 'household_id,category_id,month' })
      if (upErr) throw upErr
      return rows.length
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['budgets'] }),
  })
}
